<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { listTemplates, type DesignTemplateSummary } from '../../../services/designApi'
import { useDesignStore } from '../../../store/useDesignStore'
import { getWidgetConfig } from '../../../runtime/widgetConfig'

type Category = { id: number; name: string; slug: string; parentId: number | null }

const props = defineProps<{ productId: number; categoryId: number; sizeId: number | null }>()
const store = useDesignStore()
const items = ref<DesignTemplateSummary[]>([])
const categories = ref<Category[]>([])
const loading = ref(false)
const error = ref('')
const query = ref('')
/** 'all' | 'season' | Kategorie-ID der Obergruppe bzw. Einzelkategorie */
const group = ref<'all' | 'season' | number>('all')
const sub = ref<number | null>(null)

/** Kategorien (Slugs), die je Monat als „Passt jetzt“ vorgeschlagen werden */
const SEASON_BY_MONTH: Record<number, string[]> = {
  1: ['winter', 'neujahr'],
  2: ['valentinstag', 'winter'],
  3: ['fruehling', 'ostern'],
  4: ['ostern', 'fruehling', 'kommunion'],
  5: ['muttertag', 'pfingsten', 'kommunion', 'fruehling', 'hochzeit'],
  6: ['pfingsten', 'sommer', 'hochzeit', 'abschluss'],
  7: ['sommer', 'urlaub', 'abschluss'],
  8: ['sommer', 'urlaub', 'schule'],
  9: ['schule', 'herbst', 'urlaub'],
  10: ['herbst', 'halloween', 'weihnachten'],
  11: ['weihnachten', 'herbst', 'winter'],
  12: ['weihnachten', 'winter', 'neujahr'],
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await listTemplates({ editorBaseUrl: String((getWidgetConfig() as any)?.editorBaseUrl || ''), productId: props.productId, categoryId: props.categoryId, sizeId: props.sizeId })
    items.value = res.items
    categories.value = res.categories || []
  } catch (e: any) {
    error.value = e?.error || 'Vorlagen konnten nicht geladen werden'
  } finally {
    loading.value = false
  }
}

const byId = computed(() => new Map(categories.value.map((c) => [c.id, c])))
function rootOf(id: number | null): number | null {
  let c = id != null ? byId.value.get(id) : undefined
  while (c?.parentId != null && byId.value.has(c.parentId)) c = byId.value.get(c.parentId)
  return c?.id ?? null
}
const counts = computed(() => {
  const m = new Map<number, number>()
  for (const t of items.value) {
    if (t.categoryId == null) continue
    m.set(t.categoryId, (m.get(t.categoryId) || 0) + 1)
    const r = rootOf(t.categoryId)
    if (r != null && r !== t.categoryId) m.set(r, (m.get(r) || 0) + 1)
  }
  return m
})
/** Obergruppen bzw. Kategorien ohne Eltern, die Vorlagen enthalten */
const groups = computed(() => categories.value.filter((c) => c.parentId == null && (counts.value.get(c.id) || 0) > 0))
const subs = computed(() => typeof group.value === 'number' ? categories.value.filter((c) => c.parentId === group.value && (counts.value.get(c.id) || 0) > 0) : [])
const seasonIds = computed(() => {
  const slugs = SEASON_BY_MONTH[new Date().getMonth() + 1] || []
  return new Set(categories.value.filter((c) => slugs.includes(c.slug)).map((c) => c.id))
})
const seasonCount = computed(() => items.value.filter((t) => t.categoryId != null && seasonIds.value.has(t.categoryId)).length)

const visible = computed(() => {
  const q = query.value.trim().toLowerCase()
  return items.value.filter((t) => {
    if (group.value === 'season' && !(t.categoryId != null && seasonIds.value.has(t.categoryId))) return false
    if (typeof group.value === 'number') {
      if (sub.value != null ? t.categoryId !== sub.value : rootOf(t.categoryId) !== group.value) return false
    }
    if (!q) return true
    const cat = t.categoryId != null ? byId.value.get(t.categoryId)?.name || '' : ''
    return [t.name, t.description || '', cat, ...(t.tags || [])].join(' ').toLowerCase().includes(q)
  })
})

function pick(g: 'all' | 'season' | number) {
  group.value = g
  sub.value = null
}
function apply(t: DesignTemplateSummary) {
  if (!t.document) return
  if (store.activeDoc && store.activeDoc.layers.some((l) => l.type !== 'image' || l.source.kind !== 'placeholder')) {
    if (!window.confirm('Vorlage anwenden? Deine aktuelle Anordnung wird ersetzt (Rückgängig möglich).')) return
  }
  store.applyTemplate(t as DesignTemplateSummary & { document: NonNullable<DesignTemplateSummary['document']> })
}
onMounted(load)
watch(() => [props.productId, props.sizeId], load)
</script>

<template>
  <div class="tpl">
    <input v-if="items.length > 6" v-model="query" class="tpl__search" type="search" placeholder="Vorlage suchen, z. B. Geburtstag" aria-label="Vorlagen durchsuchen" />
    <div v-if="groups.length > 1 || seasonCount" class="tpl__chips" role="tablist" aria-label="Kategorien">
      <button type="button" class="tpl__chip" :class="{ 'is-active': group === 'all' }" @click="pick('all')">Alle</button>
      <button v-if="seasonCount" type="button" class="tpl__chip tpl__chip--season" :class="{ 'is-active': group === 'season' }" @click="pick('season')">Passt jetzt</button>
      <button v-for="c in groups" :key="c.id" type="button" class="tpl__chip" :class="{ 'is-active': group === c.id }" @click="pick(c.id)">{{ c.name }}</button>
    </div>
    <div v-if="subs.length > 1" class="tpl__chips tpl__chips--sub">
      <button type="button" class="tpl__chip" :class="{ 'is-active': sub === null }" @click="sub = null">Alle</button>
      <button v-for="c in subs" :key="c.id" type="button" class="tpl__chip" :class="{ 'is-active': sub === c.id }" @click="sub = c.id">{{ c.name }}</button>
    </div>

    <p v-if="loading" class="tpl__hint">Vorlagen werden geladen…</p>
    <p v-else-if="error" class="tpl__hint tpl__hint--err">{{ error }}</p>
    <p v-else-if="!items.length" class="tpl__hint">Für dieses Format gibt es noch keine Vorlagen.</p>
    <p v-else-if="!visible.length" class="tpl__hint">Keine passende Vorlage gefunden.</p>
    <div class="tpl__grid">
      <button v-for="t in visible" :key="t.id" type="button" class="tpl__item" :title="t.description || t.name" @click="apply(t)">
        <span class="tpl__preview"><img v-if="t.previewFilledUrl || t.previewUrl" :src="t.previewFilledUrl || t.previewUrl || ''" :alt="t.name" loading="lazy" draggable="false" /><span v-else class="tpl__ph">{{ t.slotCount }} Bilder</span></span>
        <span class="tpl__name">{{ t.name }}<em v-if="t.isPremium"> · Premium</em></span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.tpl { display: grid; gap: 10px; padding: 12px; }
.tpl__search { width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.14)); border-radius: 8px; font: inherit; font-size: 13px; background: #fff; }
.tpl__search:focus { outline: none; border-color: var(--pfe-color-accent, #e30b17); }
.tpl__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.tpl__chips--sub { padding-top: 2px; border-top: 1px dashed var(--pfe-color-border, rgba(15, 23, 42, 0.14)); padding-top: 8px; }
.tpl__chip { padding: 4px 10px; border: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.14)); border-radius: 999px; background: #fff; font: inherit; font-size: 12px; line-height: 1.4; color: inherit; cursor: pointer; white-space: nowrap; }
.tpl__chip:hover { border-color: var(--pfe-color-accent, #e30b17); }
.tpl__chip.is-active { background: var(--pfe-color-text, #0f172a); border-color: var(--pfe-color-text, #0f172a); color: #fff; }
.tpl__chip--season:not(.is-active) { border-color: #d9be8c; background: #fbf6ec; }
.tpl__hint { font-size: 12px; color: var(--pfe-color-muted, rgba(15, 23, 42, 0.62)); }
.tpl__hint--err { color: var(--pfe-color-accent, #e30b17); }
.tpl__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; }
.tpl__item { display: grid; gap: 5px; padding: 0; border: 0; background: none; font: inherit; font-size: 12px; cursor: pointer; text-align: left; }
.tpl__preview { display: block; aspect-ratio: 4 / 3; background: #ece7e1; border-radius: 8px; overflow: hidden; border: 1px solid var(--pfe-color-border, rgba(15, 23, 42, 0.1)); transition: border-color 0.15s, box-shadow 0.15s; }
.tpl__item:hover .tpl__preview, .tpl__item:focus-visible .tpl__preview { border-color: var(--pfe-color-accent, #e30b17); box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12); }
.tpl__preview img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.25s; }
.tpl__item:hover .tpl__preview img { transform: scale(1.04); }
.tpl__ph { display: grid; place-items: center; height: 100%; color: var(--pfe-color-muted, rgba(15, 23, 42, 0.62)); }
.tpl__name { padding: 0 2px; line-height: 1.3; }
.tpl__name em { font-style: normal; color: var(--pfe-color-accent, #e30b17); }
</style>
